import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { animate, motion, useMotionValue } from "framer-motion";
import { ChevronUp, ChevronDown } from "lucide-react";

function AnimatedNumber({ value }) {
  const motionValue = useMotionValue(0);
  const decimals = Number.isInteger(value) ? 0 : 2;
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const format = (v) => Number(v.toFixed(decimals));
    setDisplay(format(motionValue.get()));
    const unsubscribe = motionValue.onChange((v) => setDisplay(format(v)));
    return unsubscribe;
  }, [motionValue, decimals]);

  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: 0.8,
      ease: "easeOut",
    });
    return controls.stop;
  }, [value, motionValue]);

  return <>{display}</>;
}

export const KPI_BAR_COLORS = [
  "hsl(217 91% 55%)",
  "hsl(142 71% 45%)",
  "hsl(38 92% 50%)",
  "hsl(0 72% 51%)",
];

const KPICard = ({ value, previousValue, delta, title, barColor }) => {
  const numericValue = Number.isFinite(Number(value)) && value !== null && value !== "" ? Number(value) : 0;
  const numericDelta = typeof delta === "number" ? delta : null;
  const isPositive = numericDelta > 0;
  const isNegative = numericDelta < 0;
  const deltaColor = isPositive ? "hsl(142 71% 45%)" : isNegative ? "hsl(0 72% 51%)" : "hsl(150 10% 46%)";
  const DeltaIcon = isPositive ? ChevronUp : isNegative ? ChevronDown : null;

  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        "&:hover": { boxShadow: "0 1px 1px rgba(16,24,40,0.12)" },
      }}
    >
      <Box sx={{ height: 4, width: "100%", backgroundColor: barColor || KPI_BAR_COLORS[0] }} />
      <CardContent sx={{ p: "1.25rem" }}>
        <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", letterSpacing: 0.8, textTransform: "uppercase" }}>
          {title}
        </Typography>

        <Box sx={{ mt: 1.5, height: 40 }}>
          <Typography
            sx={{
              fontSize: 30,
              lineHeight: 1,
              fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              color: "hsl(150 30% 12%)",
              letterSpacing: -0.3,
            }}
          >
            <AnimatedNumber value={numericValue} />
          </Typography>
        </Box>

        {numericDelta !== null && (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
            {DeltaIcon && <DeltaIcon size={12} color={deltaColor} />}
            <Typography component="span" sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: deltaColor }}>
              {numericDelta > 0 ? "+" : ""}{numericDelta}%
            </Typography>
            {previousValue !== null && previousValue !== undefined && (
              <Typography component="span" sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", ml: 0.5 }}>
                vs {previousValue}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
